/**
 * Suppression définitive d'un projet — dialogue de confirmation, sur le modèle de
 * `DeleteOrganizationDialog` : inventaire (`GET /projects/:id/deletion-preview`) d'abord, bouton
 * actif seulement si le nom tapé correspond exactement (le serveur revérifie et refuse en 400
 * `CONFIRMATION_MISMATCH`), 401 `REAUTH_REQUIRED` traité ici par un bouton « Se reconnecter ».
 *
 * Succès → à la charge de l'appelant (`onDeleted`) : lui seul sait où rediriger.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { reconnectForReauth } from '../lib/reauth.js';
import { useAsync } from '../hooks/useAsync.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { Modal } from '../components/Modal.jsx';
import { TypeToConfirmField, typeToConfirmMatches } from '../components/TypeToConfirmField.jsx';
import { BTN_GHOST_SM, BTN_PRIMARY_SM } from '../ui/theme.js';

export function DeleteProjectDialog({ projectId, projectName, onClose, onDeleted }) {
  const { t } = useTranslation();
  const { user } = useSession();
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reauthRequired, setReauthRequired] = useState(false);

  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/deletion-preview`, { signal }),
    [projectId],
  );
  const preview = previewData?.data;

  const matches = typeToConfirmMatches(confirmName, projectName);

  const confirmDelete = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    setReauthRequired(false);
    try {
      await api.delete(`/projects/${projectId}`, { body: { confirmName } });
      onDeleted();
    } catch (deleteError) {
      if (deleteError.status === 401 && deleteError.details?.code === 'REAUTH_REQUIRED') {
        setReauthRequired(true);
      } else {
        setError(deleteError.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const reconnect = () => reconnectForReauth(`/projets/${projectId}?vue=reglages`, user);

  return (
    <Modal title={t('settings.delete.dialogTitle', { name: projectName })} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {previewLoading ? (
          <p className="text-sm text-ink-soft">{t('settings.delete.loading')}</p>
        ) : previewError ? (
          <p role="alert" className="text-sm text-ink">
            {t('settings.delete.loadError', { message: previewError.message })}
          </p>
        ) : (
          <>
            <p className="text-sm text-ink">{t('settings.delete.intro')}</p>

            <ul className="flex flex-col gap-1 text-sm text-ink-soft">
              <li>{t('settings.delete.inventory.tasks', { count: preview.tasks })}</li>
              <li>{t('settings.delete.inventory.comments', { count: preview.comments })}</li>
              <li>
                {t('settings.delete.inventory.activeShares', { count: preview.activeShares })}
              </li>
              {preview.members > 0 && (
                <li>{t('settings.delete.inventory.members', { count: preview.members })}</li>
              )}
            </ul>

            <TypeToConfirmField
              id="delete-project-confirm-name"
              label={t('settings.delete.confirmLabel', { name: projectName })}
              value={confirmName}
              onChange={setConfirmName}
              disabled={busy}
            />

            {reauthRequired ? (
              <div className="flex flex-col items-start gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2">
                <p role="alert" className="text-sm text-accent-ink">
                  {t('settings.delete.reauthRequired')}
                </p>
                <button type="button" className={BTN_GHOST_SM} onClick={reconnect}>
                  {t('settings.delete.reconnect')}
                </button>
              </div>
            ) : (
              error && (
                <p role="alert" className="text-sm text-ink">
                  {t('settings.delete.deleteFailed', { message: error })}
                </p>
              )
            )}

            <div className="flex justify-end gap-2">
              <button type="button" className={BTN_GHOST_SM} disabled={busy} onClick={onClose}>
                {t('settings.delete.cancel')}
              </button>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                disabled={busy || !matches}
                onClick={confirmDelete}
              >
                {t('settings.delete.confirmButton')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
